export enum EnemyState {
  Dormant = "Dormant",
  Idle = "Idle",
  Investigate = "Investigate",
  Chase = "Chase",
  Attack = "Attack",
  Pain = "Pain",
  Resurrection = "Resurrection",
  Dead = "Dead",
}

export interface EnemyStimulus {
  readonly seesPlayer: boolean;
  readonly heardPlayer: boolean;
  readonly inAttackRange: boolean;
  readonly hasLineOfSight: boolean;
  readonly reachedLastKnownPosition: boolean;
}

const DEFAULT_STIMULUS: EnemyStimulus = {
  seesPlayer: false,
  heardPlayer: false,
  inAttackRange: false,
  hasLineOfSight: false,
  reachedLastKnownPosition: false,
};

export class EnemyStateMachine {
  public state = EnemyState.Dormant;
  private stateTime = 0;
  private targetMemoryRemaining = 0;
  private painRemaining = 0;
  private resurrectionRemaining = 0;

  public constructor(
    private readonly targetMemoryDuration = 4,
    private readonly painDuration = 0.28,
  ) {}

  public update(deltaSeconds: number, stimulus: EnemyStimulus = DEFAULT_STIMULUS): EnemyState {
    if (this.state === EnemyState.Dead) {
      return this.state;
    }
    if (this.state === EnemyState.Resurrection) {
      this.resurrectionRemaining -= deltaSeconds;
      if (this.resurrectionRemaining > 0) return this.state;
      this.transition(EnemyState.Idle);
      return this.state;
    }
    this.stateTime += deltaSeconds;
    this.targetMemoryRemaining = Math.max(0, this.targetMemoryRemaining - deltaSeconds);

    if (this.state === EnemyState.Pain) {
      this.painRemaining -= deltaSeconds;
      if (this.painRemaining > 0) {
        return this.state;
      }
      this.transition(this.targetMemoryRemaining > 0 ? EnemyState.Chase : EnemyState.Idle);
    }

    if (stimulus.seesPlayer) {
      this.targetMemoryRemaining = this.targetMemoryDuration;
    }

    if (this.state === EnemyState.Dormant) {
      if (stimulus.seesPlayer) {
        this.transition(EnemyState.Chase);
      } else if (stimulus.heardPlayer) {
        this.targetMemoryRemaining = this.targetMemoryDuration;
        this.transition(EnemyState.Investigate);
      }
      return this.state;
    }

    if (this.state === EnemyState.Idle) {
      if (stimulus.seesPlayer) {
        this.transition(EnemyState.Chase);
      } else if (stimulus.heardPlayer) {
        this.targetMemoryRemaining = this.targetMemoryDuration;
        this.transition(EnemyState.Investigate);
      }
      return this.state;
    }

    if (
      stimulus.inAttackRange &&
      stimulus.hasLineOfSight &&
      (stimulus.seesPlayer || this.targetMemoryRemaining > 0)
    ) {
      this.transition(EnemyState.Attack);
    } else if (stimulus.seesPlayer || this.targetMemoryRemaining > 0) {
      this.transition(EnemyState.Chase);
    } else if (stimulus.reachedLastKnownPosition) {
      this.transition(EnemyState.Idle);
    } else {
      this.transition(EnemyState.Investigate);
    }
    return this.state;
  }

  public awakenByAlly(): void {
    if (this.state === EnemyState.Dormant || this.state === EnemyState.Idle) {
      this.targetMemoryRemaining = this.targetMemoryDuration;
      this.transition(EnemyState.Investigate);
    }
  }

  public receiveDamage(): void {
    if (this.state === EnemyState.Dead) {
      return;
    }
    this.targetMemoryRemaining = this.targetMemoryDuration;
    this.painRemaining = this.painDuration;
    this.transition(EnemyState.Pain);
  }

  public kill(): void {
    this.transition(EnemyState.Dead);
  }

  public resurrect(durationSeconds = 0.64): void {
    if (this.state !== EnemyState.Dead) return;
    this.targetMemoryRemaining = 0;
    this.painRemaining = 0;
    this.resurrectionRemaining = durationSeconds;
    this.transition(EnemyState.Resurrection);
  }

  public get elapsedInState(): number {
    return this.stateTime;
  }

  private transition(nextState: EnemyState): void {
    if (nextState !== this.state) {
      this.state = nextState;
      this.stateTime = 0;
    }
  }
}
